import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { Spinner } from 'reactstrap';
import AccordionSection from '../../../../components/AccordionSection';
import RecordBinderCard from '../../../../components/RecordBinderCard';
import RecordSops from '../../../../components/RecordSops';
import RecordInstruction from '../../../../components/RecordInstruction';
import RecordPickerModal from '../../../../components/RecordPickerModal';
import RecordFiltersModal from '../../../../components/RecordFiltersModal';
import Preview from '../../../../components/Preview';
import AppContainer from '../../../../components/AppContainer';
import PBIcon from '../../../../components/PBIcon';
import NoRecordsFound from '../../../../components/NoRecordsFound';
import withRouter from '../../../../routes/withRouter';
import RecordBinderHelper from '@progressivebeef/shared/src/helpers/RecordBinderHelper';
import localization from '@progressivebeef/shared/src/localization/i18n';
import {
  getLocale,
  getToken,
} from '@progressivebeef/shared/src/services/storageService/GlobalData';
import { baseUrl } from '@progressivebeef/shared/src/services/api';
import { globalHelper } from '@progressivebeef/shared/src/helpers';
import APIConstants from '@progressivebeef/shared/src/constants/APIConstants';
import { ATTACHMENT_TYPES } from '@progressivebeef/shared/src/constants/FormConstants';
import AssetConstants from '@progressivebeef/shared/src/constants/AssetConstants';
import {
  RECORD_BINDER_STATUS,
  ERROR_SCREEN_TYPES,
} from '@progressivebeef/shared/src/constants/AppConstants';
import RouteConstants from '@progressivebeef/shared/src/constants/RouteConstants';
import {
  resetRecordBinderProps,
  resetRecordBinderErrorProps,
  getRecordHistoryRequest,
  getRecordHistoryDetailsRequest,
  deleteRecordRequest,
  filterRecordHistory,
} from '@progressivebeef/shared/src/store/actions/recordBinder';
import {
  showLoader,
  hideLoader,
} from '@progressivebeef/shared/src/store/actions/view';
import {
  showSuccessAlert,
  showErrorAlert,
} from '../../../../components/Alerts';
import printJS from 'print-js';
import './style.scss';

const PDF_TYPES = [ATTACHMENT_TYPES.PDF, ATTACHMENT_TYPES.PDF_WEB];
const PNG_TYPES = [ATTACHMENT_TYPES.PNG, ATTACHMENT_TYPES.PNG_WEB];

class RecordBinderDetail extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      recordBinder: {},
      recordDetailsCollapse: false,
      recordHistoryCollapse: true,
      instructionsExpanded: false,
      pickerModalVisible: false,
      selectedDocument: null,
      showFilterModal: false,
      showPreview: false,
      previewSource: null,
      previewType: null,
      previewDocumentId: null,
      previewRecord: null,
      attachments: [],
      currentAttachmentIndex: 0,
      isFetchingAttachment: false,
    };

    this.toggleRecordDetails = this.toggleRecordDetails.bind(this);
    this.toggleRecordHistory = this.toggleRecordHistory.bind(this);
  }

  openPickerModal = (item) => {
    this.setState({ pickerModalVisible: true, selectedDocument: item });
  };

  closePickerModal = () => {
    this.setState({ pickerModalVisible: false, selectedDocument: null });
  };

  handleViewDocument = () => {
    const { selectedDocument } = this.state;

    if (!selectedDocument || !selectedDocument.id) {
      showErrorAlert(localization[getLocale()].ATTACHMENT_REQUIRED);
      this.closePickerModal();
      return;
    }

    const documentId = selectedDocument.id;
    this.closePickerModal();

    if (!navigator.onLine) {
      showErrorAlert(localization[getLocale()].NO_INTERNET_CONNECTION);
      return;
    }

    this.setState({
      previewDocumentId: documentId,
      previewRecord: selectedDocument,
    });
    this.props.getRecordHistoryDetails({ id: documentId });
  };

  handleEditDocument = () => {
    const { selectedDocument, recordBinder } = this.state;
    this.closePickerModal();

    if (!navigator.onLine) {
      showErrorAlert(localization[getLocale()].NO_INTERNET_CONNECTION);
      return;
    }

    if (selectedDocument && recordBinder) {
      this.props.history.push(RouteConstants.ADD_RECORD, {
        currentRecordBinder: recordBinder,
        currentRecord: selectedDocument,
      });
    }
  };

  handleDeleteDocument = () => {
    const { selectedDocument, recordBinder } = this.state;

    if (!navigator.onLine) {
      showErrorAlert(localization[getLocale()].NO_INTERNET_CONNECTION);
      return;
    }

    if (selectedDocument && selectedDocument.id) {
      this.props.deleteRecord({
        id: selectedDocument.id,
        recordBinder: recordBinder,
      });
      this.closePickerModal();
    }
  };

  closePreview = () => {
    // Invalidate any in-flight fetch so it can't reopen the modal if it
    // resolves after the user has already closed it.
    this.currentAttachmentPreviewId = null;
    this.setState({
      showPreview: false,
      previewSource: null,
      previewType: null,
      previewDocumentId: null,
      previewRecord: null,
      attachments: [],
      currentAttachmentIndex: 0,
    });
  };

  handlePrint = () => {
    const { previewSource, previewType } = this.state;
    if (!previewSource) {
      return;
    }

    if (PDF_TYPES.includes(previewType)) {
      RecordBinderHelper.printPDF(previewSource, printJS, (error) => {
        showErrorAlert('Failed to print document');
      });
    } else {
      const imageType = PNG_TYPES.includes(previewType)
        ? ATTACHMENT_TYPES.PNG
        : ATTACHMENT_TYPES.JPEG;
      RecordBinderHelper.printImage(
        previewSource,
        imageType,
        printJS,
        (error) => {
          showErrorAlert('Failed to print document');
        },
      );
    }
  };

  // Called once the GET_RECORD_HISTORY_DETAILS response for the tapped
  // document arrives - resolves the (possibly multi-item) attachments list
  // and loads the first one.
  showPreview = (recordHistoryDetails) => {
    const { previewRecord, recordBinder } = this.state;
    const { attachments } = RecordBinderHelper.getInitialValues(
      previewRecord,
      recordHistoryDetails,
      recordBinder,
    );

    if (!attachments || attachments.length === 0) {
      showErrorAlert(localization[getLocale()].ATTACHMENT_REQUIRED);
      this.setState({ previewDocumentId: null, previewRecord: null });
      return;
    }

    this.setState({ attachments, previewDocumentId: null }, () => {
      this.loadAttachmentAtIndex(0);
    });
  };

  loadAttachmentAtIndex = async (index) => {
    const attachment = this.state.attachments[index];
    if (!attachment) {
      return;
    }

    const rawType = (
      attachment.fileType ||
      attachment.extension ||
      ''
    ).toLowerCase();
    const previewType =
      rawType === ATTACHMENT_TYPES.PDF
        ? ATTACHMENT_TYPES.PDF
        : rawType === ATTACHMENT_TYPES.JPG || rawType === ATTACHMENT_TYPES.JPEG
          ? ATTACHMENT_TYPES.JPEG
          : ATTACHMENT_TYPES.PNG;

    // Legacy pre-migration records carry the content inline - preview
    // immediately. Clear any in-flight remote fetch's claim so it can't
    // land after this and overwrite it.
    if (attachment.base64) {
      this.currentAttachmentPreviewId = null;
      this.setState({
        currentAttachmentIndex: index,
        showPreview: true,
        previewSource: attachment.base64,
        previewType,
      });
      return;
    }

    if (!attachment.attachmentId) {
      showErrorAlert(localization[getLocale()].ATTACHMENT_REQUIRED);
      return;
    }

    // Guards against a stale fetch landing late: if the user taps
    // previous/next again before this one resolves, only the most
    // recently requested attachmentId is allowed to apply its result.
    const requestId = attachment.attachmentId;
    this.currentAttachmentPreviewId = requestId;

    this.setState({ isFetchingAttachment: true });

    if (!navigator.onLine) {
      this.setState({ isFetchingAttachment: false });
      showErrorAlert(localization[getLocale()].NO_INTERNET_CONNECTION);
      return;
    }

    try {
      const uri = `${baseUrl}${APIConstants.GET_ATTACHMENT}`.replace(
        '{{id}}',
        attachment.attachmentId,
      );
      const base64 = await globalHelper.getFileFromServer(uri, {
        token: getToken(),
      });
      if (this.currentAttachmentPreviewId !== requestId) {
        return;
      }
      // Only advance the index/counter once the new attachment has
      // actually loaded - on failure the previous one stays shown, so the
      // counter and arrows should stay pointed at it too.
      this.setState({
        currentAttachmentIndex: index,
        isFetchingAttachment: false,
        showPreview: true,
        previewSource: base64,
        previewType,
      });
    } catch (error) {
      if (this.currentAttachmentPreviewId !== requestId) {
        return;
      }
      this.setState({ isFetchingAttachment: false });
      showErrorAlert(
        localization[getLocale()].SOMETHING_WENT_WRONG ||
          'Something went wrong',
      );
    }
  };

  handlePreviousAttachment = () => {
    const { currentAttachmentIndex } = this.state;
    if (currentAttachmentIndex > 0) {
      this.loadAttachmentAtIndex(currentAttachmentIndex - 1);
    }
  };

  handleNextAttachment = () => {
    const { currentAttachmentIndex, attachments } = this.state;
    if (currentAttachmentIndex < attachments.length - 1) {
      this.loadAttachmentAtIndex(currentAttachmentIndex + 1);
    }
  };

  onFilterPress = () => {
    this.setState({ showFilterModal: true });
  };

  onFilterApply = (filters) => {
    const { startDate, endDate } = filters;
    const { recordBinder } = this.state;
    const filterPayload = { startDate, endDate };

    this.props.filterRecordHistory(filterPayload);
    this.setState({ showFilterModal: false });

    if (recordBinder?.id) {
      this.props.getRecordHistory({
        recordId: recordBinder.id,
        update: false,
        filter: filterPayload,
      });
    }
  };

  onFilterClear = () => {
    const { recordBinder } = this.state;
    const filterPayload = {
      startDate: null,
      endDate: null,
    };

    this.props.filterRecordHistory(filterPayload);
    this.setState({ showFilterModal: false });

    if (recordBinder?.id) {
      this.props.getRecordHistory({
        recordId: recordBinder.id,
        update: false,
        filter: filterPayload,
      });
    }
  };

  onFilterCancel = () => {
    this.setState({ showFilterModal: false });
  };

  componentDidMount() {
    const { user } = this.props;

    if (!RecordBinderHelper.canUserViewRecordBinder(user)) {
      this.props.history.push(RouteConstants.DASHBOARD);
      return;
    }

    const { currentRecordBinder } = this.props.location?.state || {};
    let recordBinder = currentRecordBinder;
    if (!recordBinder) {
      recordBinder = this.props.currentRecordBinder;
    }

    this.setState({ recordBinder });
    this.props.getRecordHistory({ recordId: recordBinder.id });
  }

  componentDidUpdate(prevProps) {
    const {
      deleteSuccess,
      deleteError,
      recordBinder,
      isDeleting,
      showLoaderView,
      hideLoaderView,
      recordHistoryDetails,
      isHistoryDetailsLoading,
      error,
    } = this.props;
    const {
      recordBinder: stateRecordBinder,
      previewDocumentId,
      isFetchingAttachment,
    } = this.state;

    if (isDeleting || isHistoryDetailsLoading || isFetchingAttachment) {
      showLoaderView();
    } else {
      hideLoaderView();
    }

    if (
      previewDocumentId &&
      prevProps.isHistoryDetailsLoading &&
      !isHistoryDetailsLoading
    ) {
      if (recordHistoryDetails) {
        this.showPreview(recordHistoryDetails);
      } else if (error && error !== prevProps.error) {
        showErrorAlert(error && (error.message || error));
        this.setState({ previewDocumentId: null, previewRecord: null });
      }
    }

    if (prevProps.deleteSuccess !== deleteSuccess && deleteSuccess) {
      showSuccessAlert(localization[getLocale()].RECORD_DELETED_SUCCESSFULLY, [
        {
          text: localization[getLocale()].OK,
          onPress: () => {
            const currentRecordBinder = stateRecordBinder || recordBinder;
            if (currentRecordBinder?.id) {
              this.props.getRecordHistory({ recordId: currentRecordBinder.id });
            }
          },
        },
      ]);
    }

    if (prevProps.deleteError !== deleteError && deleteError) {
      showErrorAlert(deleteError && (deleteError.message || deleteError), [
        {
          text: localization[getLocale()].OK,
          onPress: () => {},
        },
      ]);
    }
  }

  toggleRecordDetails() {
    this.setState({ recordDetailsCollapse: !this.state.recordDetailsCollapse });
  }

  toggleRecordHistory() {
    this.setState({ recordHistoryCollapse: !this.state.recordHistoryCollapse });
  }

  onRetryRecordHistory = () => {
    if (!navigator.onLine) {
      showErrorAlert(localization[getLocale()].OFFLINE_MODE_RETRY_ERROR);
      return;
    }
    const { recordBinder } = this.state;
    if (recordBinder?.id) {
      this.props.resetRecordBinderErrorProps();
      this.props.getRecordHistory({ recordId: recordBinder.id });
    }
  };

  toggleInstructions = () => {
    this.setState({ instructionsExpanded: !this.state.instructionsExpanded });
  };

  renderLeftHeader = () => {
    return (
      <div className="head-icon left-align">
        <PBIcon
          name={'chevron-left'}
          onPress={() => {
            this.props.history.goBack();
          }}
        />
      </div>
    );
  };

  renderRightHeader = () => {
    const { recordBinder } = this.state;
    const { user, historyFilter } = this.props;
    const hasActiveFilters = historyFilter?.startDate || historyFilter?.endDate;
    const isActive = recordBinder?.status === RECORD_BINDER_STATUS.ACTIVE;
    const isExpired = recordBinder?.status === RECORD_BINDER_STATUS.EXPIRED;
    const isArchived = recordBinder?.status === RECORD_BINDER_STATUS.ARCHIVED;
    const isAdminArchived =
      recordBinder?.admin_status === RECORD_BINDER_STATUS.ARCHIVED;
    const hasWritePermission =
      RecordBinderHelper.canUserWriteRecordBinder(user);

    const showAddButton =
      (isActive || isExpired) &&
      !isArchived &&
      !(isExpired && isAdminArchived) &&
      hasWritePermission;

    return (
      <div>
        <div className="head-icon right-align">
          {showAddButton && (
            <div className="hald-icon-box">
              <PBIcon
                name={'plus'}
                onPress={() => {
                  if (!navigator.onLine) {
                    showErrorAlert(
                      localization[getLocale()].NO_INTERNET_CONNECTION,
                    );
                    return;
                  }
                  this.props.history.push(RouteConstants.ADD_RECORD, {
                    currentRecordBinder: recordBinder,
                  });
                }}
              />
            </div>
          )}

          <div className="hald-icon-box">
            <div onClick={this.onFilterPress}>
              <img
                alt="description"
                src={
                  hasActiveFilters
                    ? AssetConstants.recordFilterFilled
                    : AssetConstants.recordFilter
                }
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  renderPreviewLeftHeader = () => {
    return (
      <div className="head-icon left-align">
        <PBIcon name={'chevron-left'} onPress={this.closePreview} />
      </div>
    );
  };

  renderPreviewRightHeader = () => {
    const { previewType } = this.state;
    const isPDF = PDF_TYPES.includes(previewType);
    const isImage = !!previewType && !isPDF;

    return (
      <div className="head-icon right-align">
        <div className="hald-icon-box">
          {(isPDF || isImage) && (
            <PBIcon name={'print'} onPress={this.handlePrint} />
          )}
        </div>
      </div>
    );
  };

  render() {
    const {
      recordBinder,
      showFilterModal,
      recordDetailsCollapse,
      recordHistoryCollapse,
      instructionsExpanded,
      pickerModalVisible,
      selectedDocument,
      showPreview,
      previewSource,
      previewType,
      attachments,
      currentAttachmentIndex,
    } = this.state;
    const { isHistoryLoading, historyFilter, recordHistory, error } =
      this.props;

    const isBin = recordBinder?.status === RECORD_BINDER_STATUS.BIN;

    const historyExpiry = selectedDocument
      ? RecordBinderHelper.getHistoryExpiryStatus(
          selectedDocument.expiresOn,
          localization[getLocale()],
        )
      : null;
    const isSelectedHistoryExpired =
      historyExpiry?.status === RECORD_BINDER_STATUS.EXPIRED;

    const hasHistory = recordHistory && recordHistory.length > 0;
    const isNoInternetHistory = !navigator.onLine && !hasHistory;
    const isServerErrorHistory =
      !isNoInternetHistory &&
      error &&
      Object.keys(error).length !== 0 &&
      !hasHistory;

    const historyScreenType = isNoInternetHistory
      ? ERROR_SCREEN_TYPES.NETWORK_ERROR
      : isServerErrorHistory
        ? ERROR_SCREEN_TYPES.SERVER_ERROR
        : ERROR_SCREEN_TYPES.NO_RECORDS;

    const shouldShowHistoryPlaceholder = !isHistoryLoading && !hasHistory;

    const isPreviewPDF = PDF_TYPES.includes(previewType);
    const previewImageType = PNG_TYPES.includes(previewType)
      ? ATTACHMENT_TYPES.PNG
      : ATTACHMENT_TYPES.JPEG;
    const previewTitle =
      localization[getLocale()].RECORD_PREVIEW +
      (attachments.length > 1
        ? ` (${currentAttachmentIndex + 1}/${attachments.length})`
        : '');

    return (
      <AppContainer
        title={localization[getLocale()].RECORD_HISTORY_TITLE}
        iconHeader
        IconRightHeader={this.renderRightHeader}
        IconLeftHeader={this.renderLeftHeader}
        noncollapsible
        history={this.props.history}>
        <RecordFiltersModal
          show={showFilterModal}
          onRequestClose={this.onFilterCancel}
          onApply={this.onFilterApply}
          onClear={this.onFilterClear}
          selectedStartDate={historyFilter?.startDate}
          selectedEndDate={historyFilter?.endDate}
          hideStatus={true}
        />

        <div className="record-binder-detail-container">
          <AccordionSection
            isOpen={recordDetailsCollapse}
            onToggle={this.toggleRecordDetails}
            label={localization[getLocale()].RECORD_DETAILS}
            showDivider={recordDetailsCollapse}>
            <div className="record-details-content">
              <RecordSops sops={recordBinder?.sops} />
              <RecordInstruction
                instructions={recordBinder?.instructions}
                expanded={instructionsExpanded}
                onToggle={this.toggleInstructions}
                truncate={RecordBinderHelper.truncateInstructions}
              />
            </div>
          </AccordionSection>

          <AccordionSection
            isOpen={recordHistoryCollapse}
            onToggle={this.toggleRecordHistory}
            label={localization[getLocale()].RECORD_HISTORY}>
            <div className="record-history-list">
              {shouldShowHistoryPlaceholder ? (
                <div className="record-history-placeholder">
                  <NoRecordsFound
                    screenType={historyScreenType}
                    onButtonPress={
                      historyScreenType === ERROR_SCREEN_TYPES.NETWORK_ERROR ||
                      historyScreenType === ERROR_SCREEN_TYPES.SERVER_ERROR
                        ? this.onRetryRecordHistory
                        : undefined
                    }
                  />
                </div>
              ) : isHistoryLoading ? (
                <div className="bottom-infinite-loader">
                  <Spinner color={'red'} />
                </div>
              ) : (
                recordHistory.map((item, idx) => (
                  <RecordBinderCard
                    key={item.id || idx}
                    title={recordBinder?.record_name}
                    item={item}
                    onCardPress={() => this.openPickerModal(item)}
                    isHistory={true}
                  />
                ))
              )}
            </div>
          </AccordionSection>
        </div>

        <RecordPickerModal
          show={pickerModalVisible}
          onRequestClose={this.closePickerModal}
          onViewDocument={this.handleViewDocument}
          onEditDocument={this.handleEditDocument}
          onDeleteDocument={this.handleDeleteDocument}
          readOnly={
            isBin ||
            !RecordBinderHelper.canUserWriteRecordBinder(this.props.user) ||
            this.state.recordBinder?.status === RECORD_BINDER_STATUS.ARCHIVED
          }
          isEdit={
            !isBin &&
            RecordBinderHelper.canUserWriteRecordBinder(this.props.user) &&
            this.state.recordBinder?.status !== RECORD_BINDER_STATUS.ARCHIVED &&
            this.state.recordBinder?.status !== RECORD_BINDER_STATUS.EXPIRED &&
            !isSelectedHistoryExpired
          }
          isDelete={
            !isBin &&
            RecordBinderHelper.canUserWriteRecordBinder(this.props.user) &&
            this.state.recordBinder?.status !== RECORD_BINDER_STATUS.ARCHIVED &&
            !(
              this.state.recordBinder?.status ===
                RECORD_BINDER_STATUS.EXPIRED &&
              this.state.recordBinder?.admin_status ===
                RECORD_BINDER_STATUS.ARCHIVED
            )
          }
        />

        {showPreview && (
          <Preview
            modalVisible={showPreview}
            leftHeader
            rightHeader
            title={previewTitle}
            pdfSource={isPreviewPDF ? previewSource : null}
            imageSource={!isPreviewPDF ? previewSource : null}
            type={!isPreviewPDF ? previewImageType : null}
            isBase64={true}
            isComment={false}
            IconLeftHeader={this.renderPreviewLeftHeader}
            IconRightHeader={this.renderPreviewRightHeader}
            showPreviousArrow={
              attachments.length > 1 && currentAttachmentIndex > 0
            }
            showNextArrow={
              attachments.length > 1 &&
              currentAttachmentIndex < attachments.length - 1
            }
            onPreviousPress={this.handlePreviousAttachment}
            onNextPress={this.handleNextAttachment}
            isLoadingNext={this.state.isFetchingAttachment}
            history={this.props.history}
          />
        )}
      </AppContainer>
    );
  }
}

const mapStateToProps = (state) => ({
  currentRecordBinder: state.recordBinder.currentRecordBinder,
  isLoading: state.recordBinder.isLoading,
  error: state.recordBinder.error,
  recordHistory: state.recordBinder.recordHistory,
  isHistoryLoading: state.recordBinder.isHistoryLoading,
  recordHistoryDetails: state.recordBinder.recordHistoryDetails,
  isHistoryDetailsLoading: state.recordBinder.isHistoryDetailsLoading,
  isDeleting: state.recordBinder.isDeleting,
  deleteSuccess: state.recordBinder.deleteSuccess,
  deleteError: state.recordBinder.error,
  historyFilter: state.recordBinder.historyFilter,
  user: state.authentication.user,
});


const mapDispatchToProps = (dispatch) => {
  return {
    getRecordHistory: (payload) => dispatch(getRecordHistoryRequest(payload)),
    getRecordHistoryDetails: (payload) =>
      dispatch(getRecordHistoryDetailsRequest(payload)),
    deleteRecord: (payload) => dispatch(deleteRecordRequest(payload)),
    filterRecordHistory: (payload) => dispatch(filterRecordHistory(payload)),
    resetRecordBinderProps: () => dispatch(resetRecordBinderProps()),
    resetRecordBinderErrorProps: () => dispatch(resetRecordBinderErrorProps()),
    showLoaderView: (payload) => dispatch(showLoader(payload)),
    hideLoaderView: (payload) => dispatch(hideLoader(payload)),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(RecordBinderDetail));

